import type { AppState, SpectateSession } from '../state/store';
import type { EngineEvent, SpectateFrame, SpectateRunSummary, SpectateWaystationPreview } from '../api/types';
import { btn, esc, pill, row } from './components';

const OUTCOME_PILL: Record<SpectateRunSummary['status'], 'ok' | 'down' | 'muted'> = {
  extracted: 'ok',
  died: 'down',
  incomplete: 'muted',
};

function outcomeLabel(status: SpectateRunSummary['status']): string {
  if (status === 'extracted') return 'extracted';
  if (status === 'died') return 'died';
  return 'demo';
}

function runCard(run: SpectateRunSummary): string {
  const banked = run.bankedGold !== null ? ` · banked ${esc(run.bankedGold)} Gold` : '';
  return `
    <article class="spectate-card">
      <div class="spectate-card__head">
        <h3>${esc(run.title)}</h3>
        ${pill(outcomeLabel(run.status), OUTCOME_PILL[run.status])}
      </div>
      <p class="muted">${esc(run.subtitle)}</p>
      <p class="hint">${esc(run.playerLabel)} · ${esc(run.runType)} · floors reached ${esc(run.floorsReached)} · ${esc(run.turns)} turns${banked}</p>
      <div class="btn-row">${btn('▶ Watch replay', 'spectate-open', { run: run.id }, { variant: 'primary' })}</div>
    </article>`;
}

function waystationTeaser(preview: SpectateWaystationPreview | null): string {
  if (!preview) return '';
  const stations = preview.stations
    .map((s) => `<li><strong>${esc(s.name)}</strong> ${pill('locked', 'muted')}<span class="muted"> — ${esc(s.blurb)}</span></li>`)
    .join('');
  return `
    <section class="card spectate-teaser">
      <h2>${esc(preview.title)}</h2>
      <p class="muted">${esc(preview.blurb)}</p>
      <ul class="spectate-stations">${stations}</ul>
      <p class="hint">${esc(preview.note)}</p>
    </section>`;
}

function runList(state: AppState): string {
  const runs = state.spectateRuns ?? [];
  const cards = runs.length
    ? runs.map(runCard).join('')
    : '<p class="muted">No demo runs available right now.</p>';
  return `
    <div class="spectate">
      <section class="card">
        <div class="spectate__head">
          <h2>Spectate</h2>
          ${pill('read-only', 'muted')}
        </div>
        <p class="muted">Watch a curated descent rendered with the real engine. You can step through it or let it play — but you can’t act, loot, or earn. Connect a wallet to descend for real.</p>
        <div class="spectate-list">${cards}</div>
      </section>
      ${waystationTeaser(state.spectatePreview)}
      <div class="btn-row">
        ${btn('← Back to home', 'goto-landing', {}, { variant: 'ghost' })}
        ${state.session ? btn('Enter the Waystation', 'goto-waystation', {}, { variant: 'primary' }) : btn('Connect Wallet', 'goto-dashboard', {}, { variant: 'primary' })}
      </div>
    </div>`;
}

function frameStats(frame: SpectateFrame): string {
  const run = frame.run;
  return `
    <div class="stats">
      ${row('HP', `<strong class="${run.hp <= 3 ? 'danger' : ''}">${esc(run.hp)}</strong> / ${esc(run.maxHp)}`)}
      ${row('Lantern', `${esc(run.lanternFuel)} ${run.lanternFuel === 0 ? pill('dark!', 'down') : ''}`)}
      ${row('Burden', String(run.burden))}
      ${row('Carried Gold', `<strong>${esc(run.carriedGold)}</strong>`)}
      ${row('Floor / Turn', `${esc(run.floor)} / ${esc(run.turn)}`)}
      ${row('Type', pill(run.type, run.type === 'reward' ? 'ok' : 'muted'))}
    </div>`;
}

function eventText(event: EngineEvent): string {
  switch (event.type) {
    case 'supply_used':
      return event.stat === 'lantern'
        ? `${event.supplyKey} restores ${event.actualAppliedAmount} Lantern (${event.before} -> ${event.after})`
        : `${event.supplyKey} restores ${event.actualAppliedAmount} HP (${event.before} -> ${event.after}${event.afterTurn !== undefined && event.afterTurn !== event.after ? `, after response ${event.afterTurn}` : ''})`;
    case 'enemy_damaged':
      return event.killed ? `${event.enemyId} slain (${event.damage} dmg)` : `${event.enemyId} hit for ${event.damage} (HP ${event.hp})`;
    case 'enemy_moved':
      return `${event.enemyId} closes in`;
    case 'player_damaged':
      return event.source === 'enemy'
        ? `Runner took ${event.damage} from ${event.enemyId ?? 'enemy'} (HP ${event.hp})`
        : `Runner took ${event.damage} from the dark (HP ${event.hp})`;
  }
}

function eventLog(frame: SpectateFrame): string {
  const lines = [`<div class="combat-log__line">${esc(frame.logLine)}</div>`];
  for (const event of frame.events) lines.push(`<div>${esc(eventText(event))}</div>`);
  return `<div class="combat-log" aria-live="polite">${lines.join('')}</div>`;
}

function outcomeBanner(session: SpectateSession, frame: SpectateFrame): string {
  if (!frame.terminal) return '';
  const s = session.summary;
  if (s.status === 'extracted') {
    return `<div class="banner banner--notice">⛵ Extracted — banked <strong>${esc(s.bankedGold ?? 0)}</strong> Gold. (Demo — no real economy.)</div>`;
  }
  if (s.status === 'died') {
    return `<div class="banner banner--error" role="status">☠ The dark took them on floor ${esc(frame.floor)}. Carried haul lost.</div>`;
  }
  return '';
}

function replayPlayer(session: SpectateSession): string {
  const frame = session.frames[session.index] ?? session.frames[0]!;
  const atStart = session.index <= 0;
  const atEnd = session.index >= session.frames.length - 1;
  const playPause = session.playing
    ? btn('⏸ Pause', 'spectate-pause', {}, { variant: 'ghost' })
    : btn('▶ Play', 'spectate-play', {}, { variant: 'primary', disabled: atEnd });
  return `
    <div class="spectate spectate--replay">
      <section class="run__primary">
        <div class="run__head">
          <div>
            <h2>${esc(session.summary.title)}</h2>
            <p class="hint">${esc(session.summary.playerLabel)} · ${esc(session.summary.runType)} · spectating</p>
          </div>
          ${btn('← Back to runs', 'spectate-close', {}, { variant: 'ghost' })}
        </div>
        <div class="banner banner--notice">Read-only spectate — no actions, no rewards, no economy.</div>
        ${outcomeBanner(session, frame)}
        ${frameStats(frame)}
        <div class="run__stage-panel">
          <div class="gamefeel-stage-wrap">
            <canvas class="gamefeel-stage" data-gamefeel-stage width="640" height="360" aria-label="Spectated dungeon stage"></canvas>
          </div>
        </div>
        <div class="spectate-controls">
          <div class="btn-row">
            ${btn('⏮ Reset', 'spectate-reset', {}, { variant: 'ghost', disabled: atStart })}
            ${btn('◀ Prev', 'spectate-prev', {}, { variant: 'ghost', disabled: atStart })}
            ${playPause}
            ${btn('Next ▶', 'spectate-next', {}, { variant: 'ghost', disabled: atEnd })}
          </div>
          <p class="hint">Step ${esc(session.index + 1)} / ${esc(session.frames.length)} · turn ${esc(frame.turn)} · floor ${esc(frame.floor)}</p>
        </div>
      </section>
      <aside class="run__side">
        <h2>What happened</h2>
        ${eventLog(frame)}
        <p class="muted">The Marketplace, Gold Exchange, Spinner, and stash are holder-only — connect a wallet to unlock them.</p>
      </aside>
    </div>`;
}

export function renderSpectate(state: AppState): string {
  if (state.spectateSession) return replayPlayer(state.spectateSession);
  return runList(state);
}
